import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import * as API from '../api/teachers';
import { Teacher, CreateTeacherData, UpdateTeacherData } from '../api/teachers';

interface TeachersContextType {
    teachers: Teacher[];
    isLoading: boolean;
    error: string | null;
    addTeacher: (data: CreateTeacherData) => Promise<Teacher>;
    updateTeacher: (id: string, data: UpdateTeacherData) => Promise<Teacher>;
    deleteTeacher: (id: string) => Promise<void>;
    refreshTeachers: () => Promise<void>;
}

const TeachersContext = createContext<TeachersContextType | undefined>(undefined);

export const useTeachers = () => {
    const context = useContext(TeachersContext);
    if (context === undefined) {
        throw new Error('useTeachers must be used within a TeachersProvider');
    }
    return context;
};

interface TeachersProviderProps {
    children: ReactNode;
}

export const TeachersProvider: React.FC<TeachersProviderProps> = ({ children }) => {
    const [teachers, setTeachers] = useState<Teacher[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const refreshTeachers = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const data = await API.getTeachers();
            setTeachers(data);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Erreur lors du chargement des professeurs');
            console.error('Error fetching teachers:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        refreshTeachers();
    }, [refreshTeachers]);

    const addTeacher = async (data: CreateTeacherData): Promise<Teacher> => {
        try {
            const newTeacher = await API.createTeacher(data);
            setTeachers(prev => [...prev, newTeacher]);
            return newTeacher;
        } catch (err: any) {
            throw new Error(err.response?.data?.message || 'Erreur lors de la création du professeur');
        }
    };

    const updateTeacher = async (id: string, data: UpdateTeacherData): Promise<Teacher> => {
        try {
            const updatedTeacher = await API.updateTeacher(id, data);
            setTeachers(prev => prev.map(teacher => teacher.id === id ? updatedTeacher : teacher));
            return updatedTeacher;
        } catch (err: any) {
            throw new Error(err.response?.data?.message || 'Erreur lors de la mise à jour du professeur');
        }
    };

    const deleteTeacher = async (id: string): Promise<void> => {
        try {
            await API.deleteTeacher(id);
            setTeachers(prev => prev.filter(teacher => teacher.id !== id));
        } catch (err: any) {
            throw new Error(err.response?.data?.message || 'Erreur lors de la suppression du professeur');
        }
    };

    const value: TeachersContextType = {
        teachers,
        isLoading,
        error,
        addTeacher,
        updateTeacher,
        deleteTeacher,
        refreshTeachers,
    };

    return (
        <TeachersContext.Provider value={value}>
            {children}
        </TeachersContext.Provider>
    );
};
